import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { QueryKey } from '@/Config/api/queryKey';
import type { Passkey } from '@/Domain/api/User/dto/Passkey.dto';
import { getPasskeySetupToken } from '@/Domain/utils/authCallbackParams';
import { isPasskeyCeremonyCancelled } from '@/Domain/utils/passkeyErrors';

const TOO_MANY_REQUESTS_STATUS = 429;

export const PasskeySetupError = {
  invalid: 'invalid',
  rejected: 'rejected',
  limit: 'limit',
  throttled: 'throttled',
  generic: 'generic',
} as const;

export type PasskeySetupErrorKind = (typeof PasskeySetupError)[keyof typeof PasskeySetupError];

const ERROR_BY_CODE: Record<string, PasskeySetupErrorKind> = {
  INVITATION_INVALID: PasskeySetupError.invalid,
  PASSKEY_VERIFICATION_FAILED: PasskeySetupError.rejected,
  PASSKEY_LIMIT_REACHED: PasskeySetupError.limit,
};

type ApiErrorBody = { errors?: Record<string, { message: string; code: string }[]> };

const toErrorKind = (error: unknown): PasskeySetupErrorKind => {
  const axiosError = error as AxiosError<ApiErrorBody>;
  if (axiosError.response?.status === TOO_MANY_REQUESTS_STATUS) {
    return PasskeySetupError.throttled;
  }
  const code = axiosError.response?.data?.errors?.root?.[0]?.code;
  return (code && ERROR_BY_CODE[code]) || PasskeySetupError.generic;
};

export const usePasskeySetup = () => {
  const [token] = useState(getPasskeySetupToken);
  const [error, setError] = useState<PasskeySetupErrorKind | null>(
    token ? null : PasskeySetupError.invalid,
  );

  const mutation = useMutation<Passkey, unknown, { token: string }>({
    mutationKey: [QueryKey.accounts.passkeyInvitationSetup],
    throwOnError: false,
    onError: (mutationError) => {
      if (isPasskeyCeremonyCancelled(mutationError)) {
        return;
      }
      setError(toErrorKind(mutationError));
    },
  });

  const setUpPasskey = () => {
    setError(null);
    mutation.mutate({ token });
  };

  return {
    setUpPasskey,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    error,
  };
};
